import { call, put, takeLeading, select, delay } from "redux-saga/effects";
import { SubscriptionAPI } from "services/subscription";

import {
  getConfirmationInfo,
  getConfirmationInfoFailure,
  getConfirmationInfoSuccess,
  sendOTP,
  sendOTPSuccess,
  sendOTPFailure,
  reSendOTP,
  reSendOTPSuccess,
  reSendOTPFailure,
  confirmPayment,
  confirmPaymentSuccess,
  confirmPaymentFailure,
  verifyReceiveAccount,
  verifyReceiveAccountSuccess,
  verifyReceiveAccountFailure,
  checkPreApprove,
  checkPreApproveSuccess,
  checkPreApproveFailure,
  clearUseLombard,
  manualSetUnitAndBaht,
} from "redux/reducers/subscription";

import { MasterSelector, ProductSelector, SubscriptionSelector } from "redux/selector";
import { CustomerSelector } from "redux/selector/customer-selector";
import { getAddressCompact } from "./master";
import {
  getAccountByAccountNo,
  isSecurityTypeDBT,
  isSecurityTypeCOM,
  unitToBaht,
  bahtToUnit,
} from "utils/product-order-util";
import { ReceiveMethod, ErrorType, AcceptType, BrokerCode, BrokerShortName } from "const";
import { signOut } from "redux/reducers/auth";
import { openModal } from "redux/reducers/central-modal";
import { isTitleOther } from "utils/customer-util";
import { filterFields } from "utils/common-util";
import { alertError, getTitleSuccess } from "redux/reducers/master";
import { checkTitleAgainstGender } from "utils/personal-info-util";
import { ProductAPI } from "services/product";
import { clearLombardDetail, updateProductLombardDetail } from "redux/reducers/product";
import { rounding } from "utils/math";
import { isValidKtxAccountNumber } from "utils/form";

function* getConfirmationInfoSaga(action) {
  try {
    const payload = action.payload;
    const isSecurityOffshoreNote = payload.isSecurityOffshoreNote;
    const isComplexProduct = isSecurityTypeCOM(payload.securityType);
    const useLombard = yield select(SubscriptionSelector.useLombard);

    const response = yield call(
      SubscriptionAPI.getConfirmationInfo,
      useLombard
        ? { lombardFlag: "Y", securityCode: payload.securityCode, structureNoteFlag: isComplexProduct ? "Y" : null }
        : { securityCode: payload.securityCode },
    );

    let info = response.data.result;

    // remove default receive if product securityType is 'DBT'
    if (payload && !useLombard) {
      if (
        (isSecurityTypeDBT(payload.securityType) && info.receiveMethod === ReceiveMethod.TSD) ||
        (payload.allowReceiveMethods && !payload.allowReceiveMethods.includes(info.receiveMethod))
      ) {
        delete info.receiveMethod;
      }

      if (isComplexProduct) {
        info.receiveMethod = ReceiveMethod.POSTAL;
      }
    }

    if (isComplexProduct && useLombard) {
      info.receiveMethod = ReceiveMethod.POSTAL;
      info.mailingAddress += ` (${info.receiveAccountNumber})`;
    }

    if (info.receiveMailing) {
      delete info.receiveMethod;
    }

    if (isComplexProduct && isSecurityOffshoreNote) {
      info.receiveMethod = ReceiveMethod.BROKER;
      info.receiveSecurityCompanyCode = BrokerCode.CUSTODIAN;
      if (payload.osnDetail?.osnCustodianAccount) {
        info.receiveAccountNumber = payload.osnDetail.osnCustodianAccount;
      } else {
        delete info.receiveAccountNumber;
      }
    }

    // ส่วนนี้ตรวจสอบความเข้ากันได้ระหว่างโบรกเกอร์ที่เลือกและหลักทรัพย์
    const hasBrokerInfo = info.receiveSecurityCompanyCode || info.receiveAccountNumber;
    if (hasBrokerInfo) {
      const productDetail = yield select(ProductSelector.productDetail);
      const brokerList = yield select(MasterSelector.brokerList);
      const selectedBroker = brokerList.find(
        (broker) => broker.securityCompanyCode === info.receiveSecurityCompanyCode,
      );

      /**
       * ตรวจสอบความไม่สอดคล้องกันระหว่างโบรกเกอร์และหลักทรัพย์ โดยเกิดขึ้นในกรณีใดกรณีหนึ่งต่อไปนี้:
       * 1. ไม่พบโบรกเกอร์ที่เลือกในระบบ
       * 2. โบรกเกอร์ไม่รองรับ Bond Connect แต่หลักทรัพย์ต้องการ Bond Connect
       */
      const isBrokerNotFound = !selectedBroker;
      const isBondConnectBroker = selectedBroker?.bondConnect === "Y";
      // กรณีผู้ใช้เลือก broker ที่ไม่รองรับ Bond Connect แต่โปรดักท์ต้องการ Bond Connect
      const isSelectedNonBondConnectBrokerButProductRequiresBondConnect =
        !isBondConnectBroker && productDetail.isSecurityBondConnect;
      const isBrokerSecurityMismatch = isBrokerNotFound || isSelectedNonBondConnectBrokerButProductRequiresBondConnect;

      // หากมีความไม่สอดคล้องกัน ให้ลบข้อมูลโบรกเกอร์ออกเพื่อให้ผู้ใช้เลือกใหม่
      if (isBrokerSecurityMismatch) {
        delete info.receiveSecurityCompanyCode;
        delete info.receiveSecurityCompanyName;
        delete info.receiveAccountNumber;
      } else if (
        selectedBroker.securityCompanyShortName === BrokerShortName.KTX &&
        !isValidKtxAccountNumber(info.receiveAccountNumber)
      ) {
        // Clear an invalid KTX account number saved in the customer's profile.
        delete info.receiveAccountNumber;
      }
    }

    // Address Text (Province, District, SubDistrict)
    const addressTxt = yield call(
      getAddressCompact,
      info.mailingProvince,
      info.mailingDistrict,
      info.mailingSubDistrict,
    );
    //* address for case lombard + complex product
    if (info.customerAddress) {
      const customerAddressTxt = yield call(
        getAddressCompact,
        info.customerAddress.mailingProvince,
        info.customerAddress.mailingDistrict,
        info.customerAddress.mailingSubDistrict,
      );

      info.customerAddress.mailingProvinceTxt = customerAddressTxt[0];
      info.customerAddress.mailingDistrictTxt = customerAddressTxt[1];
      info.customerAddress.mailingSubDistrictTxt = customerAddressTxt[2];
    }

    // check gender and title matched
    const checkResult = checkTitleAgainstGender(info);
    info = checkResult.info;
    if (checkResult.prepareTitle) {
      yield put(getTitleSuccess({ options: checkResult.titles, thai: info.thai }));
    }

    yield put(
      getConfirmationInfoSuccess({
        ...info,
        mailingProvinceTxt: addressTxt[0],
        mailingDistrictTxt: addressTxt[1],
        mailingSubDistrictTxt: addressTxt[2],
      }),
    );
    action.payload?.onComplete?.();
  } catch (error) {
    yield put(getConfirmationInfoFailure(error));
  }
}

const sendOTPSaga = function* ({ payload }) {
  try {
    const sendFirstOTP = yield select(SubscriptionSelector.sendFirstOTP);
    if (!sendFirstOTP) {
      const response = yield call(SubscriptionAPI.sendOTP, payload);
      const refOtp = response.data.result.refOtp;
      yield put(sendOTPSuccess(refOtp));
    }
  } catch (error) {
    yield put(sendOTPFailure(error));
  }
};

const reSendOTPSaga = function* ({ payload }) {
  try {
    // const response = yield call(SubscriptionAPI.reSendOTP, payload);
    const response = yield call(SubscriptionAPI.sendOTP, payload);
    const refOtp = response.data.result.refOtp;
    yield put(reSendOTPSuccess(refOtp));
  } catch (error) {
    yield put(reSendOTPFailure(error));
  }
};

const confirmPaymentSaga = function* ({ payload: payloadOTP }) {
  // const payloadModal = {
  //   code: '43949',
  //   state: { message: 'ยอดจองซื้อหลักทรัพย์คงเหลือ 10,000 บาท (Code:43970)' },
  //   actions: {
  //     onEdit: payloadOTP.backToEditUnit,
  //   },
  // }
  // yield put(openModal(payloadModal))
  // yield put(confirmPaymentFailure({ ignore: true }))
  try {
    const subscriptionDetail = yield select(SubscriptionSelector.subscriptionDetail);
    const productDetail = yield select(ProductSelector.productDetail);
    const locale = yield select(MasterSelector.locale);

    let payload = {
      ...productDetail,
      ...subscriptionDetail,
      otp: payloadOTP.data.otp,
      purchaseUnit: +subscriptionDetail.unit,
      totalPayment: +rounding(+subscriptionDetail.baht, 2),
      language: locale,
    };
    const accounts = yield select(CustomerSelector.accounts);

    // payment account
    payload.payAccountNo = payload.accountNo;
    delete payload.accountNo;
    payload.accountPayment = getAccountByAccountNo(payload.payAccountNo, accounts);
    payload.payAccountNoDisplay = payload.accountPayment?.accountNoDisplay;
    payload.payAccountName = payload.accountPayment?.accountName;
    payload.payAccountType = payload.accountPayment?.productType;
    payload.payAccountBranch = payload.accountPayment?.brnOwnerShip;
    payload.payAccountBranchName = ""; // mock

    // receive account
    payload.accountReceive = getAccountByAccountNo(payload.rfdAccountNo, accounts);
    payload.rfdAccountNoDisplay = payload.accountReceive?.accountNoDisplay;
    payload.rfdAccountName = payload.accountReceive?.accountName;
    payload.rfdAccountType = payload.accountReceive?.productType;
    payload.rfdAccountBranch = payload.accountReceive?.brnOwnerShip;
    payload.rfdAccountBranchName = ""; // mock

    //complexProduct receive account ccy 1
    if (isSecurityTypeCOM(productDetail)) {
      let ccyList = productDetail.receiveAccountCcy.split(",");

      if (payload.rfdAccountNoCcy1) {
        if (ccyList[0] === "THB") {
          payload.accountReceiveCcy1 = getAccountByAccountNo(payload.rfdAccountNoCcy1, accounts);
        } else {
          payload.accountReceiveCcy1 = getAccountByAccountNo(payload.rfdAccountNoCcy1, accounts);
        }
        payload.rfdAccountNoDisplayCcy1 = payload.accountReceiveCcy1?.accountNoDisplay;
        payload.rfdAccountNameCcy1 = payload.accountReceiveCcy1?.accountName;
        payload.rfdAccountTypeCcy1 = payload.accountReceiveCcy1?.productType;
        payload.rfdAccountBranchCcy1 = payload.accountReceiveCcy1?.brnOwnerShip;
        payload.rfdAccountCurrencyCodeCcy1 = payload.accountReceiveCcy1?.currCd;
      }
      //complexProduct receive account ccy 2
      if (payload.rfdAccountNoCcy2) {
        if (ccyList[1] === "THB") {
          payload.accountReceiveCcy2 = getAccountByAccountNo(payload.rfdAccountNoCcy2, accounts);
        } else {
          payload.accountReceiveCcy2 = getAccountByAccountNo(payload.rfdAccountNoCcy2, accounts);
        }
        payload.rfdAccountNoDisplayCcy2 = payload.accountReceiveCcy2?.accountNoDisplay;
        payload.rfdAccountNameCcy2 = payload.accountReceiveCcy2?.accountName;
        payload.rfdAccountTypeCcy2 = payload.accountReceiveCcy2?.productType;
        payload.rfdAccountBranchCcy2 = payload.accountReceiveCcy2?.brnOwnerShip;
        payload.rfdAccountCurrencyCodeCcy2 = payload.accountReceiveCcy2?.currCd;
      }
    }

    // title
    if (payload.isGenderAndTitleInvalid && isTitleOther(payload.title)) {
      payload.title = payload.titleOther;
    }

    // disclaimer list
    payload.disclaimerList = Object.values(subscriptionDetail.disclaimerMapping ?? {}).filter((id) => {
      const n = Number(id);
      return Number.isFinite(n) && n > 0;
    });

    // filter fields
    payload = filterFields(payload, confirmPaymentFields);
    const response = yield call(SubscriptionAPI.confirmPayment, payload);
    yield delay(1000);
    const responseReservationRights = yield call(SubscriptionAPI.checkReservationRights, {
      securityCode: productDetail.securityCode,
    });

    const paymentInfo = response.data.result;
    paymentInfo.subscriptions = paymentInfo.subscriptions.map((info) => {
      return { ...subscriptionDetail, ...info };
    });
    yield put(
      confirmPaymentSuccess({
        ...paymentInfo,
        reservationRights: responseReservationRights.data?.result?.autoRedeem ?? null,
      }),
    );

    // upload documents
    try {
      if (subscriptionDetail.attachDocuments?.length) {
        const formData = new FormData();
        formData.append("substId", paymentInfo.substId);
        for (const { fileUrl, ...fileObj } of subscriptionDetail.attachDocuments) {
          const blob = yield fetch(fileUrl).then((r) => r.blob());
          const file = new File([blob], fileObj.name, {
            type: fileObj.type,
          });
          formData.append("file", file, fileObj.name);
        }
        yield call(ProductAPI.uploadDocument, formData);
      }
    } catch (error) {
      alertError(error);
    }

    payloadOTP.onComplete(); // goto summary page
  } catch (error) {
    let payloadModal = {};
    switch (error.code) {
      case ErrorType.OTP_FAIL_LIMIT_EXCEED:
        const errorConst = (yield select(MasterSelector.errorConst))?.[ErrorType.OTP_FAIL_LIMIT_EXCEED];
        yield put(signOut());
        yield put(confirmPaymentFailure({ modalMessage: errorConst.textLocale }));
        break;
      case ErrorType.SETTLE_FULLY_PURCHASE:
      case ErrorType.SETTLE_REMAIN_NOT_ENOUGH:
      case ErrorType.CBS_FULLY_PURCHASE:
        payloadModal = {
          code: error.code,
          state: { message: error.modalMessage },
          actions: {
            onEdit: payloadOTP.backToEditUnit,
          },
        };
        yield put(openModal(payloadModal));
        yield put(confirmPaymentFailure({ ...error, ignore: true }));
        break;
      case ErrorType.CBS_REMAIN_NOT_ENOUGH:
        payloadModal = {
          code: AcceptType.UnitAvaliable,
          state: { message: error.modalMessage, payload: payloadOTP },
        };
        yield put(openModal(payloadModal));
        yield put(confirmPaymentFailure({ ...error, ignore: true }));
        break;
      default:
        yield put(confirmPaymentFailure(error));
        break;
    }
  }
};

const verifyReceiveAccountSaga = function* ({ payload }) {
  try {
    if (typeof payload?.accountNo !== "string" || !payload.accountNo.trim()) {
      yield put(verifyReceiveAccountSuccess({ isAccount: false }));
      return;
    }

    const response = yield call(SubscriptionAPI.isAccountNumber, payload);
    const isAccount = response.data.result.account;
    yield put(verifyReceiveAccountSuccess({ isAccount }));
  } catch (error) {
    yield put(verifyReceiveAccountFailure(error));
  }
};

const checkPreApproveSaga = function* ({ payload: { securityCode, availableQuota, isComplexProduct } }) {
  const startTime = performance.now();
  const { sellPriceUnit } = yield select(ProductSelector.productDetail);
  const { defaultPurchaseUnit } = yield select(SubscriptionSelector.subscriptionDetail);

  try {
    const response = yield call(SubscriptionAPI.checkPreApprove, { securityCode, availableQuota });
    let availableAmount = response.data.result.availableAmount;
    let availableUnitForCheckingOnly = bahtToUnit(availableAmount, sellPriceUnit);
    const availableUnit = Math.floor(availableUnitForCheckingOnly);

    if (availableUnitForCheckingOnly % 1 !== 0) {
      availableAmount = unitToBaht(availableUnit, sellPriceUnit);
    }

    if (isComplexProduct && availableUnit !== defaultPurchaseUnit) {
      const error = new Error("Message-Error");
      error.messageId = "lombard.complex-product.miss-match-amount";
      throw error;
    }

    let payload;
    if (availableUnit < defaultPurchaseUnit) {
      payload = {
        unit: availableUnit,
        baht: availableAmount,
      };
    } else {
      payload = {
        unit: defaultPurchaseUnit,
        baht: unitToBaht(defaultPurchaseUnit, sellPriceUnit),
      };
    }

    yield put(checkPreApproveSuccess(payload));
    yield put(
      updateProductLombardDetail({
        useLombard: true,
        sellMaxUnit: availableUnit,
        lombardAvailableUnit: availableUnit,
        lombardAvailableAmount: availableAmount,
      }),
    );
  } catch (error) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    console.log("error:", error);
    yield put(checkPreApproveFailure(error));

    if (duration < 500) {
      yield delay(500 - duration);
    }

    //* product store
    yield put(clearLombardDetail());
    //* subscription store
    yield put(clearUseLombard());
    yield put(
      manualSetUnitAndBaht({
        unit: defaultPurchaseUnit,
        baht: unitToBaht(defaultPurchaseUnit, sellPriceUnit),
      }),
    );
  }
};

const saga = [
  takeLeading(getConfirmationInfo.type, getConfirmationInfoSaga),
  takeLeading(sendOTP.type, sendOTPSaga),
  takeLeading(reSendOTP.type, reSendOTPSaga),
  takeLeading(confirmPayment.type, confirmPaymentSaga),
  takeLeading(verifyReceiveAccount.type, verifyReceiveAccountSaga),
  takeLeading(checkPreApprove.type, checkPreApproveSaga),
];

export default saga;

const confirmPaymentFields = [
  "otp",
  "refOtp",
  "securityCode",
  "language",
  "email",
  "incomeSource",
  "incomeSourceOthDesc",
  "receiveMethod",
  "receiveSecurityCompanyCode",
  "receiveSecurityCompanyName",
  "receiveAccountNumber",
  "paymentMethod",
  "payAccountNo",
  "payAccountName",
  "payAccountType",
  "payAccountTypeDesc",
  "payAccountBranch",
  "payAccountBranchName",
  "payAccountNoDisplay",
  "rfdAccountNo",
  "rfdAccountName",
  "rfdAccountType",
  "rfdAccountTypeDesc",
  "rfdAccountBranch",
  "rfdAccountBranchName",
  "rfdAccountNoDisplay",
  "rfdAccountNoCcy1",
  "rfdAccountNameCcy1",
  "rfdAccountTypeCcy1",
  "rfdAccountBranchCcy1",
  "rfdAccountBranchNameCcy1",
  "rfdAccountNoDisplayCcy1",
  "rfdAccountCurrencyCodeCcy1",
  "rfdAccountNoCcy2",
  "rfdAccountNameCcy2",
  "rfdAccountTypeCcy2",
  "rfdAccountBranchCcy2",
  "rfdAccountBranchNameCcy2",
  "rfdAccountNoDisplayCcy2",
  "rfdAccountCurrencyCodeCcy2",
  "mailingAddress",
  "mailingProvince",
  "mailingDistrict",
  "mailingSubDistrict",
  "mailingZipCode",
  "acceptDiscList",
  "purchaseUnit",
  "totalPayment",
  "acId",
  "title",
  "gender",
  "ka",
  "useLombard",
  "mncQuotaId",
  "unitHolderInterest",
  "disclaimerList",
];
